import { LANDING_PATHS } from "@/lib/landingPaths";
import { DASHBOARD_ROUTES } from "@/lib/dashboardRoutes";
import { WEBSITE_LEAD_INTERCEPTOR_COPY } from "@/constants/websiteLeadInterceptor";
import { resolveRootLandingKey, getHostname } from "@/lib/siteHosts";
import { getStoredLeadId } from "@/lib/leadIdCookie";

const DEFAULT_META = {
  title: "FractoLabs | AI Digital Employees — Qualify & Book 24/7",
  description:
    "FractoLabs deploys AI digital employees for your business: greet inbound leads, qualify jobs, and book appointments 24/7. Works with your CRM and calendar. Start a free trial.",
  ogTitle: "FractoLabs | AI Digital Employees",
  ogDescription:
    "Industry-trained AI employees that qualify leads and fill your calendar 24/7. Plug into your existing CRM and messengers.",
  ogImage: "/images/logo.png",
  ogImageAlt: "FractoLabs AI digital employees",
  robots: "index, follow",
};

const ROOFING_META = {
  title: "Roofing Interceptor | AI Employee for Roofers — FractoLabs",
  description:
    "AI digital employee for roofing teams: reply in 9 seconds, qualify leak and inspection leads, and book appointments 24/7. Works with your CRM. 14-day free trial.",
  ogTitle: "Roofing Interceptor | FractoLabs",
  ogDescription:
    "While you're on the roof, your AI employee qualifies leads and books inspections in seconds — 24/7.",
  ogImage: "/images/thumbnail_roofing.png",
  ogImageAlt: "FractoLabs Roofing Interceptor AI employee",
  robots: "index, follow",
};

const PARTNER_META = {
  title: "FractoLabs Partner | Scale Agency MRR — White-Label AI Employees",
  description:
    "White-label AI Employee infrastructure for marketing agencies. Scale MRR without new hires — deploy in 72 hours, bundle into retainers, and keep $150–$400+ margin per client.",
  ogTitle: "Scale Your Agency MRR | FractoLabs Partner",
  ogDescription:
    "Add $300+ monthly margin per client with enterprise AI Employees your agency white-labels and resells — no new account managers required.",
  ogImage: "/images/logo.png",
  ogImageAlt: "FractoLabs Partner — White-Label AI Employees for Agencies",
  robots: "index, follow",
};

const INTERCEPT_PROTOCOL_META = {
  title: "Intercept Protocol | FractoLabs Partner",
  description:
    "If your clients are not on FractoLabs, your ad spend may be financing competitors. Lock your niche, protect campaigns, and activate $4.5k+ in monthly MRR.",
  ogTitle: "Intercept Protocol | FractoLabs Partner",
  ogDescription:
    "Lock your niche, protect ad campaigns, and flip lead leakage into white-label MRR with FractoLabs infrastructure.",
  ogImage: "/images/logo.png",
  ogImageAlt: "FractoLabs Intercept Protocol for agencies",
  robots: "index, follow",
};

const REAL_ESTATE_META = {
  title: "AI Broker for Real Estate | FractoLabs",
  description:
    "Automate WhatsApp conversations and get hot investors directly into your calendar. FractoLabs AI agent replies in 10 seconds and qualifies clients 24/7.",
  ogTitle: "AI Broker for Real Estate | FractoLabs",
  ogDescription:
    "Stop losing WhatsApp leads overnight. Get hot investors directly into your calendar with our AI broker that replies in 10 seconds.",
  ogImage: "/images/thumbnail.png",
  ogImageAlt: "FractoLabs AI Broker for Real Estate",
  robots: "index, follow",
};

const STUB_META = {
  title: "Alex S. | Systems & Cloud Infrastructure Architect",
  description:
    "Cloud infrastructure, automated backend pipelines, and data routing systems. Currently fully booked — not accepting new consulting projects.",
  ogTitle: "Alex S. | Systems & Cloud Infrastructure Architect",
  ogDescription: "Currently fully booked and not accepting new consulting projects or clients.",
  ogImage: "/images/logo.png",
  ogImageAlt: "",
  robots: "noindex, nofollow",
};

const ROOT_LANDING_META_BY_KEY = {
  main: DEFAULT_META,
  partner: PARTNER_META,
  roofing: ROOFING_META,
  realEstate: REAL_ESTATE_META,
  loginRedirect: STUB_META,
  stub: STUB_META,
};

const PRIVATE_ROUTE_TITLES = {
  "/dashboard": "Dashboard",
  [DASHBOARD_ROUTES.settings]: "Digital Brain Settings",
  [DASHBOARD_ROUTES.schedule]: "Digital Brain Schedule",
  "/dashboard/warm-up-sequences": "Warm-Up Sequences",
  "/dashboard/account-settings": "Account Settings",
  "/dashboard/listings": "Listings",
  "/dashboard/chat-test": "Chat Test",
  "/dashboard/chat-whatsapp": "WhatsApp Chat",
  "/dashboard/chat-telegram": "Telegram Chat",
  "/dashboard/chat-ghl": "GoHighLevel Chat",
  "/dashboard/chat-web": WEBSITE_LEAD_INTERCEPTOR_COPY.chatsPageTitle,
  "/dashboard/integrations/messengers/whatsapp": "WhatsApp Integration",
  "/dashboard/integrations/messengers/telegram": "Telegram Integration",
  "/dashboard/integrations/calendars/google": "Google Calendar",
  "/dashboard/integrations/crm/ghl": "GoHighLevel CRM",
  "/dashboard/integrations/web-widget": WEBSITE_LEAD_INTERCEPTOR_COPY.productName,
  "/dashboard/notifications": "Notifications",
  "/dashboard/billing": "Billing",
  "/partner/dashboard": "Partner Overview",
  "/partner/tiers": "Tiers & Margin",
  "/partner/deploy": "Deploy Client",
  "/partner/clients": "Clients",
  "/partner/billing": "Partner Billing",
  "/partner/settings": "Partner Settings",
  "/login": "Login",
  "/password-reset": "Password Reset",
};

const LEGAL_ROUTE_TITLES = {
  "/contact": "Contact",
  "/privacy-policy": "Privacy Policy",
  "/terms": "Terms of Service",
};

const LANDING_META_BY_PATH = {
  [LANDING_PATHS.main]: DEFAULT_META,
  [LANDING_PATHS.mainPreview]: DEFAULT_META,
  [LANDING_PATHS.partner]: PARTNER_META,
  [LANDING_PATHS.roofing]: ROOFING_META,
  [LANDING_PATHS.realEstate]: REAL_ESTATE_META,
  [LANDING_PATHS.interceptProtocol]: INTERCEPT_PROTOCOL_META,
};

const resolveLandingMeta = (pathname) => LANDING_META_BY_PATH[pathname] || DEFAULT_META;

export const getPageMeta = (pathname, search = "") => {
  if (pathname === "/login" || pathname === "/password-reset") {
    const pageName = PRIVATE_ROUTE_TITLES[pathname];
    return {
      ...DEFAULT_META,
      title: `${pageName} | FractoLabs`,
      description: DEFAULT_META.description,
      ogTitle: `${pageName} | FractoLabs`,
      robots: "noindex, nofollow",
    };
  }

  if (pathname.startsWith("/dashboard") || pathname.startsWith("/partner")) {
    const pageName = PRIVATE_ROUTE_TITLES[pathname] || "Partner Dashboard";
    return {
      ...DEFAULT_META,
      title: `${pageName} | FractoLabs`,
      description: DEFAULT_META.description,
      ogTitle: `${pageName} | FractoLabs`,
      robots: "noindex, nofollow",
    };
  }

  if (LEGAL_ROUTE_TITLES[pathname]) {
    const pageName = LEGAL_ROUTE_TITLES[pathname];
    return {
      ...DEFAULT_META,
      title: `${pageName} | FractoLabs`,
      ogTitle: `${pageName} | FractoLabs`,
      robots: "index, follow",
    };
  }

  if (pathname === "/") {
    const key = resolveRootLandingKey(getHostname(), search, getStoredLeadId());
    return ROOT_LANDING_META_BY_KEY[key] ?? STUB_META;
  }

  if (LANDING_META_BY_PATH[pathname]) {
    return resolveLandingMeta(pathname);
  }

  return DEFAULT_META;
};

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  if (!href) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const applyPageMeta = (meta) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const canonicalUrl = `${origin}${pathname === "/" ? "" : pathname}` || "https://fractolabs.com";
  const ogImage = meta.ogImage?.startsWith("http") ? meta.ogImage : `${origin}${meta.ogImage}`;

  document.title = meta.title;

  upsertMeta("name", "description", meta.description);
  upsertMeta("name", "robots", meta.robots);
  upsertMeta("property", "og:site_name", "FractoLabs");
  upsertMeta("property", "og:title", meta.ogTitle);
  upsertMeta("property", "og:description", meta.ogDescription);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", canonicalUrl);
  upsertMeta("property", "og:image", ogImage);
  upsertMeta("property", "og:image:alt", meta.ogImageAlt);
  upsertMeta("property", "og:locale", "en_US");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", meta.ogTitle);
  upsertMeta("name", "twitter:description", meta.ogDescription);
  upsertMeta("name", "twitter:image", ogImage);

  if (meta.robots?.includes("noindex")) {
    const canonical = document.querySelector('link[rel="canonical"]');
    canonical?.remove();
  } else {
    upsertCanonical(canonicalUrl);
  }
};
